// Lib
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import classNames from 'classnames';

// Utils
import { getTranslationStyles } from '../../../utils/cssUtil';
import { getUserCurrentBoardId } from '../../../../common/users/utils/userPropertyUtils';

// Selectors
import { getUsers } from '../../../user/usersSelector';

// Components
import usePrevious from '../../../utils/react/usePrevious';
import LiveCursorTypePointer from './cursorTypes/LiveCursorTypePointer';
import LiveCursorTypePen from './cursorTypes/LiveCursorTypePen';
import LiveCursorTypeEraser from './cursorTypes/LiveCursorTypeEraser';

// Constants
import { CURSOR_TYPES } from '../../../../common/users/liveCursor/liveCursorConstants';

// Styles
import './LiveCursor.scss';

const LiveCursorTypeSwitch = (props) => {
    const { cursorType } = props;

    switch (cursorType) {
        case CURSOR_TYPES.PEN:
            return <LiveCursorTypePen {...props} />;
        case CURSOR_TYPES.ERASER:
            return <LiveCursorTypeEraser {...props} />;
        default:
            return <LiveCursorTypePointer {...props} />;
    }
};

LiveCursorTypeSwitch.propTypes = {
    cursorType: PropTypes.string,
};

const mapStateToProps = createSelector(
    getUsers,
    (state, { userId }) => userId,
    (users, userId) => ({
        user: users.get(userId),
    }),
);

const useSelectionClass = ({ isSelecting }) => {
    const [selectionClass, setSelectionClass] = useState();

    const prevIsSelecting = usePrevious(isSelecting);

    const removeSelectionClass = () => setSelectionClass(null);

    useEffect(() => {
        if (isSelecting && !prevIsSelecting) {
            setSelectionClass('selecting');
        }

        if (!isSelecting && prevIsSelecting) {
            setSelectionClass('selecting-remove');
            setTimeout(removeSelectionClass, 1);
        }
    }, [isSelecting, prevIsSelecting]);

    return selectionClass;
};

const useIsResting = ({ x, y }) => {
    const restingTimeoutRef = useRef();
    const [isResting, setIsResting] = useState();

    useEffect(() => {
        if (restingTimeoutRef.current) clearTimeout(restingTimeoutRef.current);
        if (isResting) setIsResting(false);

        restingTimeoutRef.current = setTimeout(() => setIsResting(true), 3000);

        return () => {
            clearTimeout(restingTimeoutRef.current);
        };
    }, [x, y]);

    return isResting;
};

const CursorDebugState = ({ cursor: { x, y, status, cursorType }, selectionClass, resting }) => (
    <pre className="debug-state">
        {`Position: ${x}, ${y}
Status: ${status}
Type: ${cursorType}
Selection State: ${selectionClass}
Resting: ${resting}`}
    </pre>
);

CursorDebugState.propTypes = {
    cursor: PropTypes.object,
    selectionClass: PropTypes.string,
    resting: PropTypes.bool,
};

const LiveCursor = ({ cursor, currentBoardId, user, gridSize, translation = { x: 0, y: 0 }, debugShowCursorState }) => {
    const selectionClass = useSelectionClass(cursor);
    const resting = useIsResting(cursor);

    if (getUserCurrentBoardId(user) !== currentBoardId) return null;

    const { x, y, status, cursorType } = cursor;

    const cursorClasses = classNames('LiveCursor', status, cursorType, selectionClass, { resting });

    const cursorStyle = getTranslationStyles({
        x: x * gridSize + translation.x,
        y: y * gridSize + translation.y,
    });

    return (
        <div className={cursorClasses} style={cursorStyle}>
            <LiveCursorTypeSwitch cursorType={cursorType} user={user} />
            {debugShowCursorState && (
                <CursorDebugState cursor={cursor} selectionClass={selectionClass} resting={resting} />
            )}
        </div>
    );
};

LiveCursor.propTypes = {
    userId: PropTypes.string.isRequired,
    currentBoardId: PropTypes.string,
    user: PropTypes.object,
    cursor: PropTypes.object,
    translation: PropTypes.object,
    gridSize: PropTypes.number,
    debugShowCursorState: PropTypes.bool,
};

export default connect(mapStateToProps)(LiveCursor);
